import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _taggedTemplateLiteral from "@babel/runtime/helpers/taggedTemplateLiteral";
var _templateObject;
import _regeneratorRuntime from "@babel/runtime/regenerator";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import gql from 'graphql-tag';
import { print } from 'graphql';
import { useEffect, useRef, useCallback } from 'react';
import { subscribe } from './lib/aggSubscription';
import { fg } from '@atlaskit/platform-feature-flags';
var appClientEventsSubscription = gql(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n\tsubscription onAppClientEvent(\n\t\t$appId: ID!\n\t\t$channel: String!\n\t\t$contextAri: ID!\n\t\t$eventName: String\n\t) {\n\t\tecosystem {\n\t\t\tonAppClientEvent(\n\t\t\t\tappId: $appId\n\t\t\t\tchannel: $channel\n\t\t\t\tcontextAri: $contextAri\n\t\t\t\teventName: $eventName\n\t\t\t) {\n\t\t\t\tpayload\n\t\t\t}\n\t\t}\n\t}\n"])));

//data.ecosystem.onAppClientEvent

var getContextKey = function getContextKey(context) {
  return Object.entries(context).sort(function (a, b) {
    return a[0].localeCompare(b[0]);
  }).toString();
};
var unsubscribeAllSubscriptions = function unsubscribeAllSubscriptions(activeSubscriptions) {
  activeSubscriptions.forEach(function (subscription) {
    if (!subscription.closed) {
      subscription.unsubscribe();
    }
  });
  activeSubscriptions.clear();
};

/**
 * This hook is used to subscribe to client events from the extension.
 * It will return a function that can be called to unsubscribe from the client events.
 *
 * This hook has no side effect (i.e. does not actively trigger any network calls or actively making any subscription.
 * It is because everything inside this hook is lazily initialized.
 * Importantly:
 * - It only has impact when the subscribeClientEvent is called by the client (e.g. @forge/bridge)
 * - The backend service (AGG subscription) is feature flagged and is not currently live in production, hence calling this hook in production or without
 *   enabled flag will have no impact.
 *
 * @param appId The app id
 * @param contextAri The context ari
 * @returns The subscribeClientEvent function that can be called to subscribe to client events
 */
export var useSubscribeClientEventsBridge = function useSubscribeClientEventsBridge(appId, contextAri) {
  var activeSubscriptions = useRef(new Map());
  useEffect(function () {
    var currentSubscriptions = activeSubscriptions.current;
    return function () {
      unsubscribeAllSubscriptions(currentSubscriptions);
    };
  }, []);

  // this is the bridge method that will be called from the extension
  var subscribeClientEvent = useCallback( /*#__PURE__*/function () {
    var _ref = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(payload) {
      var _payload$data, context, onClientEvent, subscription, contextKey, existingSubscription;
      return _regeneratorRuntime.wrap(function _callee$(_context) {
        while (1) switch (_context.prev = _context.next) {
          case 0:
            _payload$data = payload.data, context = _payload$data.context, onClientEvent = _payload$data.onClientEvent;
            subscription = subscribe({
              operationName: 'onAppClientEvent',
              query: print(appClientEventsSubscription),
              variables: _objectSpread({
                appId: appId,
                contextAri: contextAri
              }, context)
            }, {
              next: function next(result) {
                var _result$data$ecosyste;
                var _ref2 = (_result$data$ecosyste = result.data.ecosystem.onAppClientEvent) !== null && _result$data$ecosyste !== void 0 ? _result$data$ecosyste : {},
                  payload = _ref2.payload;
                onClientEvent(payload !== null && payload !== void 0 ? payload : null);
              },
              error: function error(err) {
                onClientEvent(null, err);
              },
              complete: function complete() {}
            });
            contextKey = getContextKey(context);
            existingSubscription = activeSubscriptions.current.get(contextKey);
            if (existingSubscription && !existingSubscription.closed) {
              existingSubscription.unsubscribe();
            }
            activeSubscriptions.current.set(contextKey, subscription);
            return _context.abrupt("return", {
              unsubscribe: function unsubscribe() {
                // close the subscription
                if (!subscription.closed) {
                  subscription.unsubscribe();
                }
                activeSubscriptions.current.delete(contextKey);
              }
            });
          case 7:
          case "end":
            return _context.stop();
        }
      }, _callee);
    }));
    return function (_x) {
      return _ref.apply(this, arguments);
    };
  }(), [appId, contextAri]);
  return {
    // eslint-disable-next-line @atlaskit/platform/ensure-feature-flag-prefix
    subscribeClientEvent: fg('forge-ui-enable-websocket-support') ? subscribeClientEvent : undefined
  };
};